import React, { useCallback, useEffect, useRef, useState } from 'react'

import { createExcelBlob } from '@/utils/fileHelper'
import { Box, Button, Flex, Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { saveAs } from 'file-saver'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { RawTextCell } from '../components/Cell'
import TableFilter, { FilterType } from '../components/Filter/TableFilter'
import TablePagination from '../components/TablePagination'

const defaultColumn = {
  cell: RawTextCell,
  size: 200,
  minSize: 50,
  meta: {
    filterVariant: FilterType.SIMPLE_TEXT,
  },
}
const ClientSideTable = ({
  columnDef,
  data,
  updateData = () => {},
  disabled = false,
  showFooter = false,
  tableWidth = '',
  handleRowClick = () => {},
  canSelect = false,
  rowSelection = [],
  setRowSelection = () => {},
  hiddenColumns = [],
  grouping = [],
  textAlign = 'center',
  canDownload = false,
  downloadDataFormatter = (data) => data,
  onDownload,
  jumpToLastPageTrigger,
  ...rest
}) => {
  const tableInstance = useReactTable({
    data,
    defaultColumn,
    columns: columnDef,
    filterFns: {},
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    meta: {
      updateData: updateData,
      disabled: disabled,
    },
  })
  const [state, setState] = useState(tableInstance.initialState)
  const previousJumpTriggerRef = useRef(jumpToLastPageTrigger)

  tableInstance.setOptions((prev) => ({
    ...prev,
    state,
    onStateChange: setState,
  }))

  const handleDownload = useCallback(async () => {
    try {
      // Use filtered rows from the table (respecting active filters)
      const filteredRows = tableInstance.getFilteredRowModel().rows.map((r) => r.original)

      if (typeof onDownload === 'function') {
        await onDownload(filteredRows)
        return
      }
      const formattedData = downloadDataFormatter(filteredRows)
      const excelBlob = createExcelBlob(formattedData)
      saveAs(excelBlob, 'E-Approval GC Material.xlsx')
    } catch (err) {
      toast.error('Error in Downloading file: ' + err.message)
    }
  }, [data, onDownload, downloadDataFormatter])

  const pageIndex = tableInstance.getState().pagination.pageIndex
  const pageCount = tableInstance.getPageCount()

  useEffect(() => {
    if (pageCount === 0) {
      return
    }

    const lastPageIndex = pageCount - 1
    if (pageIndex > lastPageIndex) {
      tableInstance.setPageIndex(lastPageIndex)
    }
  }, [pageCount, pageIndex, tableInstance])

  useEffect(() => {
    if (jumpToLastPageTrigger === undefined || pageCount === 0) {
      return
    }

    // Only jump when the trigger value changes (e.g. after adding a row),
    // not when data/pageCount changes from delete or filtering.
    if (previousJumpTriggerRef.current === jumpToLastPageTrigger) {
      return
    }

    previousJumpTriggerRef.current = jumpToLastPageTrigger

    tableInstance.setPageIndex(pageCount - 1)
  }, [jumpToLastPageTrigger, pageCount, tableInstance])

  return (
    <>
      {canDownload && (
        <Flex>
          <Button colorScheme="green" onClick={handleDownload}>
            Download
          </Button>
        </Flex>
      )}
      <TablePagination table={tableInstance} dataCount={tableInstance.getPageCount()} />

      <Table
        variant="simple"
        border="1px solid rgb(226, 232, 240)"
        borderRadius="md"
        w={tableWidth === '' ? tableInstance.getCenterTotalSize() : tableWidth}
      >
        <Thead>
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  p={2}
                  textAlign={textAlign}
                  w={header.getSize() !== 0 ? header.getSize() : undefined}
                  colSpan={header.colSpan}
                  bg={header.column.columnDef?.backgroundColor}
                >
                  <Flex h="100%" direction="column" justifyContent="space-between">
                    <Flex _hover={{ cursor: 'pointer' }} w="100%" onClick={header.column.getToggleSortingHandler()}>
                      <Box w="100%"> {flexRender(header.column.columnDef.header, header.getContext())}</Box>

                      {/* Sorting */}
                      {{ asc: <FaAngleUp />, desc: <FaAngleDown /> }[`${header.column.getIsSorted()}`] ?? null}
                    </Flex>

                    {/* Filter */}
                    {header.column.getCanFilter() ? (
                      <Box mt="1">
                        <TableFilter column={header.column} updatePagination={() => tableInstance.setPageIndex(0)} />
                      </Box>
                    ) : null}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {tableInstance.getRowModel().rows.map((row, rowIndex) => {
            return (
              <Tr key={rowIndex} onClick={() => handleRowClick(row)}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    p={2}
                    textAlign={textAlign}
                    bg={cell.column.columnDef?.backgroundColor}
                    w={cell.column.getSize() !== 0 ? cell.column.getSize() : undefined}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            )
          })}
        </Tbody>

        {showFooter && (
          <Tfoot>
            {tableInstance.getFooterGroups().map((footerGroup) => (
              <Tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <Td
                    key={header.id}
                    colSpan={header.colSpan}
                    textAlign={textAlign}
                    bg={header.column.columnDef?.backgroundColor}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tfoot>
        )}
      </Table>
    </>
  )
}

export default ClientSideTable
